import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Location, useLocation, useNavigate, useParams } from 'react-router-dom'

import { transformTextParamsData } from '@pages/Parameters/helpers'

import { useYupValidationResolver } from '@hooks'
import { enrichViewActionWithTargetColumns, getColumnParametersFromArray, getEntityTypeForRequest } from '@helpers'
import { CATEGORIES, DATAGRID_HEADER_HEIGHT, ENTITY, MODEL_TYPE, ROUTES } from '@constants'

import { entityConfigurationSchema } from '@validations'

import {
  useCreateActionMutation,
  useCreateAllColumnsMutation,
  useCreateColumnViewMutation,
  useCreateMandatoryColumnsMutation,
  useCreateViewMutation,
  useDeleteActionMutation,
  useDeleteAllColumnsMutation,
  useDeleteColumnViewMutation,
  useFetchActionMutation,
  useFetchAllObjectQuery,
  useFetchColumnViewMutation,
  useFetchDictionariesByCategoryCodeQuery,
  useFetchObjectByCodeQuery,
  useFetchViewQuery,
  useUpdateActionMutation,
  useUpdateColumnTitleMutation,
  useUpdateColumnViewMutation,
  useUpdateOrderColumnsMutation,
  useUpdateViewMutation,
  useUpdateVisibilityColumnByIdMutation,
} from '../../../redux/api'
import { ActionRow, AutocompleteOption, ConfiguredColumn, EntityType } from '../../../types'

import { useHandlers } from './useHandlers'

type UseEntityCreateOrEditProps = {
  type: ENTITY
}

export const useEntityCreateOrEdit = ({ type }: UseEntityCreateOrEditProps) => {
  const [currentEntity, setCurrentEntity] = useState<EntityType>()

  const [createEntity, { isSuccess: isSuccessCreate, isLoading: entityCreateLoading }] =
    useCreateViewMutation()
  const [updateEntity, { isSuccess: isSuccessUpdate, isLoading: entityUpdateLoading }] =
    useUpdateViewMutation()
  const [fetchColumn, { data: row, fulfilledTimeStamp: timeRow, isLoading: isLoadingColumn }] =
    useFetchColumnViewMutation()

  const [createColumn] = useCreateColumnViewMutation()
  const [createAllColumns, { isLoading: createAllColumnsLoading }] = useCreateAllColumnsMutation()
  const [deleteAllColumns, { isLoading: deleteAllColumnsLoading }] = useDeleteAllColumnsMutation()
  const [createMandatoryColumns, { isLoading: createMandatoryColumnsLoading }] =
    useCreateMandatoryColumnsMutation()
  const [updateColumn] = useUpdateColumnViewMutation()
  const [deleteColumn] = useDeleteColumnViewMutation()
  const [updateColumnOrder] = useUpdateOrderColumnsMutation()
  const [updateVisibilityColumn] = useUpdateVisibilityColumnByIdMutation()
  const [
    fetchAction,
    { data: actionRow, fulfilledTimeStamp: timeAction, isLoading: isLoadingAction },
  ] = useFetchActionMutation()
  const [updateColumnTitle] = useUpdateColumnTitleMutation()
  const [createAction, { isLoading: isCreateActionLoading }] = useCreateActionMutation()
  const [deleteAction] = useDeleteActionMutation()
  const [editAction, { isLoading: isEditActionLoading }] = useUpdateActionMutation()

  const { state: locationState } = useLocation() as Location & { state?: { code?: string } }
  const { code } = useParams()
  const copyCode = locationState?.code
  const viewType = getEntityTypeForRequest(type)

  const {
    data: entity,
    isError,
    isLoading: entityIsLoading,
    isFetching: entityIsFetching,
  } = useFetchViewQuery({ code: code ?? copyCode, viewType }, { skip: !code && !copyCode })

  const resolver = useYupValidationResolver(entityConfigurationSchema)
  const methods = useForm<{
    useParameters: boolean
    hasQuickSearch: boolean
    hasWrapRowText: boolean
    hasWrapHeaderText: boolean
    headerHeight: number | null
    headerGroupHeight: number | null
    parameterShowSA: AutocompleteOption | null
  }>({
    defaultValues: {
      useParameters: true,
      hasQuickSearch: true,
      hasWrapRowText: false,
      hasWrapHeaderText: false,
      parameterShowSA: null,
      headerHeight: DATAGRID_HEADER_HEIGHT,
      headerGroupHeight: DATAGRID_HEADER_HEIGHT,
    },
    resolver,
    reValidateMode: 'onChange',
    mode: 'onChange',
    shouldUnregister: true,
  })
  const {
    watch,
    formState: { isDirty: isDirtyUseParametersModal, isValid: isValidCreateOrEditForm },
  } = methods

  const watchUseParameters = watch('useParameters')
  const watchQuickSearch = watch('hasQuickSearch')
  const watchWrapRowText = watch('hasWrapRowText')
  const watchWrapHeaderText = watch('hasWrapHeaderText')
  const watchParameterShowSA = watch('parameterShowSA')
  const watchHeaderHeight = watch('headerHeight')
  const watchHeaderGroupHeight = watch('headerGroupHeight')
  const { headerProperties, textProperties } = getColumnParametersFromArray(
    currentEntity && currentEntity.parameters ? currentEntity.parameters : []
  )

  const initialColumnHeaderStyles = useMemo(
    () => transformTextParamsData(headerProperties),
    [headerProperties, entity]
  )
  const initialColumnTextStyles = useMemo(
    () => transformTextParamsData(textProperties),
    [textProperties, entity]
  )

  const entityActionEdit = useMemo(
    () => entity?.actions?.find(action => action.eventCode?.includes('edit')),
    [entity]
  )

  const entityFormCode = entityActionEdit?.objectFormCode

  const { state, data, handlers } = useHandlers({
    entity,
    type,
    createEntity,
    updateEntity,
    createColumn,
    createAllColumns,
    deleteAllColumns,
    createMandatoryColumns,
    fetchColumn,
    updateColumn,
    updateColumnTitle,
    deleteColumn,
    fetchAction,
    deleteAction,
  })

  const { objectCode, modalProperty, showDialog } = state

  const { handleSetCurrentRow } = handlers

  const mainRows: ConfiguredColumn[] = useMemo(() => entity?.columns || [], [entity])
  const actionRows: ActionRow[] = useMemo(() => {
    const columns = entity?.columns || []

    return (
      entity?.actions?.map(action =>
        enrichViewActionWithTargetColumns(
          {
            ...action,
            params: action.params ? JSON.stringify(action.params) : null,
          },
          columns
        )
      ) || []
    )
  }, [entity])

  const { data: objects } = useFetchAllObjectQuery()
  const { data: object } = useFetchObjectByCodeQuery(objectCode, { skip: !objectCode })
  const { data: bindingValues } = useFetchDictionariesByCategoryCodeQuery(CATEGORIES.BINDING_VIEW)
  const { data: bindingValuesTypes } = useFetchDictionariesByCategoryCodeQuery(
    CATEGORIES.BINDING_TYPES_VIEW
  )
  const { data: bindingValuesColumns } = useFetchDictionariesByCategoryCodeQuery(
    CATEGORIES.BINDING_COLUMN,
    {
      skip: modalProperty !== MODEL_TYPE.COLUMN,
    }
  )
  const { data: bindingValuesColumnsTypes } = useFetchDictionariesByCategoryCodeQuery(
    CATEGORIES.BINDING_TYPES_COLUMN,
    {
      skip: modalProperty !== MODEL_TYPE.COLUMN,
    }
  )
  const { data: bindingValuesAction } = useFetchDictionariesByCategoryCodeQuery(
    CATEGORIES.BINDING_VIEW_ACTION,
    {
      skip: modalProperty !== MODEL_TYPE.VIEW_ACTION,
    }
  )

  const { data: bindingValuesActionEvents } = useFetchDictionariesByCategoryCodeQuery(
    CATEGORIES.BINDING_VIEW_EVENTS_ACTION
  )

  const navigate = useNavigate()

  useEffect(() => {
    if (entity?.columns && entity.actions) {
      setCurrentEntity(copyCode ? { ...entity, code: '' } : entity)

      return
    }
  }, [entity])

  useEffect(() => {
    const columns = entity?.columns || []

    const enrichActionRow = (action: ActionRow) => enrichViewActionWithTargetColumns(action, columns)

    const mergeActionRowWithEntity = (fetchedAction: ActionRow) => {
      const entityAction = entity?.actions?.find(
        action => String(action.id) === String(fetchedAction.id)
      )

      if (!entityAction) {
        return enrichActionRow(fetchedAction)
      }

      return enrichActionRow({
        ...entityAction,
        ...fetchedAction,
        columnIds: fetchedAction.columnIds?.length
          ? fetchedAction.columnIds
          : entityAction.columnIds,
      })
    }

    if (row && actionRow && timeRow && timeAction) {
      const selectedRow = timeRow > timeAction ? row : mergeActionRowWithEntity(actionRow)
      handleSetCurrentRow(selectedRow)

      return
    }
    if (row && !isLoadingAction) {
      handleSetCurrentRow(row)

      return
    }
    if (actionRow && !isLoadingColumn) {
      handleSetCurrentRow(mergeActionRowWithEntity(actionRow))
    }
  }, [row, actionRow, entity])

  useEffect(() => {
    if (isError) {
      navigate(ROUTES.CONFIG_VIEWS_CREATE)
    }
  }, [isError])

  useEffect(() => {
    if (!showDialog) {
      handleSetCurrentRow(null)
    }
  }, [showDialog])

  return {
    state: {
      ...state,
      currentEntity,
    },
    data: {
      ...data,
      locationState,
      objects,
      object,
      bindingValues,
      bindingValuesTypes,
      bindingValuesColumnsTypes,
      bindingValuesColumns,
      bindingValuesAction,
      bindingValuesActionEvents,
      mainRows,
      actionRows,
      entityIsLoading: entityIsLoading || entityIsFetching,
      entityUpdateLoading,
      entityCreateLoading,
      isSuccessCreate,
      isSuccessUpdate,
      objectCode,
      entityFormCode,
      initialColumnHeaderStyles,
      initialColumnTextStyles,
      methods,
      watchUseParameters,
      watchQuickSearch,
      watchWrapRowText,
      watchHeaderHeight,
      watchHeaderGroupHeight,
      watchWrapHeaderText,
      watchParameterShowSA,
      isDirtyUseParametersModal,
      isValidCreateOrEditForm,
      createAllColumnsLoading,
      deleteAllColumnsLoading,
      createMandatoryColumnsLoading,
      isActionLoading: isCreateActionLoading || isEditActionLoading,
    },
    handlers,
    mutations: {
      createAction,
      editAction,
      updateColumnOrder,
      updateVisibilityColumn,
    },
  }
}
